<script setup>
import { ref, computed, watch, nextTick, onMounted } from 'vue';
import { normalizeColorToHex, getColorLuminance, isValidColor } from '../material/material-utils';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  colorId: {
    type: String,
    default: '',
  },
  colorValue: {
    type: String,
    default: '#000000',
  },
  position: {
    type: Object,
    default: () => ({ x: 0, y: 0 }),
  },
});

const emit = defineEmits(['update', 'close', 'delete']);

// State
const localColor = ref(props.colorValue);
const inputRef = ref(null);
const popoverRef = ref(null);
const isInputFocused = ref(false);
const hasEyeDropper = ref(false);

// Constants
const POPOVER_WIDTH = 280; // Slightly wider for better layout
const POPOVER_HEIGHT = 380;
const VIEWPORT_PADDING = 16;

// Modern Design Palette
// Organized by Hue and Vibrancy
const presetColors = [
  // Grayscale (Precision)
  '#FFFFFF',
  '#FAFAFA',
  '#E5E5E5',
  '#A3A3A3',
  '#737373',
  '#404040',
  '#171717',
  '#000000',

  // Vibrant Spectrum (Design System Foundation)
  '#EF4444', // Red
  '#F97316', // Orange
  '#F59E0B', // Amber
  '#EAB308', // Yellow
  '#84CC16', // Lime
  '#22C55E', // Green
  '#10B981', // Emerald
  '#14B8A6', // Teal

  // Cool Spectrum
  '#06B6D4', // Cyan
  '#0EA5E9', // Sky
  '#3B82F6', // Blue
  '#6366F1', // Indigo
  '#8B5CF6', // Violet
  '#A855F7', // Purple
  '#D946EF', // Fuchsia
  '#EC4899', // Pink

  // Deep & Elegant (Luxury/Professional)
  '#7F1D1D', // Dark Red
  '#7C2D12', // Dark Orange
  '#713F12', // Dark Amber
  '#365314', // Dark Lime
  '#14532D', // Dark Green
  '#164E63', // Dark Cyan
  '#1E3A8A', // Dark Blue
  '#4C1D95', // Dark Violet
];

// Check EyeDropper support
onMounted(() => {
  hasEyeDropper.value = 'EyeDropper' in window;
});

// Watchers
watch(
  () => props.colorValue,
  (newVal) => {
    if (!isInputFocused.value) {
      localColor.value = newVal;
    }
  }
);

watch(
  () => props.show,
  async (show) => {
    if (show) {
      localColor.value = props.colorValue;
      await nextTick();
      inputRef.value?.focus();
      inputRef.value?.select();
    }
  }
);

// Computed
const normalizedColor = computed(() => {
  if (isValidColor(localColor.value)) {
    return normalizeColorToHex(localColor.value);
  }
  return normalizeColorToHex(props.colorValue);
});

const isValid = computed(() => isValidColor(localColor.value));

// Actions
const handleInput = (e) => {
  let value = e.target.value;
  if (value && !value.startsWith('#') && /^[0-9A-Fa-f]/.test(value)) {
    value = '#' + value;
  }
  localColor.value = value;
};

const selectPreset = (color) => {
  localColor.value = color;
  // Auto apply for better UX, or keep manual apply if desired.
  // Here we just update local state to let user preview, user clicks Apply to confirm.
  // BUT: Design tools usually instant apply. Let's instant update parent but keep popover open?
  // User asked for "Optimization". Let's stick to explicit Apply/Cancel to avoid accidental changes,
  // but maybe we can emit an 'preview' event if the architecture supported it.
  // For now, let's keep local selection -> Apply flow for safety,
  // OR if we want "Live" feel:
  // emit('update', { id: props.colorId, value: color }); // Live update
};

const handleApply = () => {
  if (isValid.value) {
    emit('update', { id: props.colorId, value: normalizedColor.value });
    emit('close');
  }
};

const handleDelete = () => {
  emit('delete', props.colorId);
  emit('close');
};

const handleEyeDropper = async () => {
  if (!window.EyeDropper) return;
  const eyeDropper = new window.EyeDropper();
  try {
    const result = await eyeDropper.open();
    localColor.value = result.sRGBHex;
  } catch {
    // User canceled
  }
};

const handleKeydown = (e) => {
  if (e.key === 'Enter') {
    e.preventDefault();
    handleApply();
  } else if (e.key === 'Escape') {
    e.preventDefault();
    emit('close');
  }
};

// Styles
const getSwatchStyle = (color) => {
  const luminance = getColorLuminance(color);
  // Add a subtle border for light colors to separate from white background
  const isLight = luminance > 200;
  return {
    backgroundColor: color,
    boxShadow: isLight ? 'inset 0 0 0 1px rgba(0,0,0,0.06)' : 'none',
  };
};

const safePosition = computed(() => {
  const { x, y } = props.position;
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1920;
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 1080;

  const halfWidth = POPOVER_WIDTH / 2;
  let safeX = x;

  if (x - halfWidth < VIEWPORT_PADDING) {
    safeX = halfWidth + VIEWPORT_PADDING;
  }
  if (x + halfWidth > viewportWidth - VIEWPORT_PADDING) {
    safeX = viewportWidth - halfWidth - VIEWPORT_PADDING;
  }

  let safeY = y + 8; // Slight offset from trigger
  let showAbove = false;

  if (safeY + POPOVER_HEIGHT > viewportHeight - VIEWPORT_PADDING) {
    showAbove = true;
    safeY = y - POPOVER_HEIGHT - 8;
  }

  // Ensure it doesn't go off top
  if (safeY < VIEWPORT_PADDING) {
    safeY = VIEWPORT_PADDING;
  }

  return { x: safeX, y: safeY, showAbove };
});

const popoverStyle = computed(() => ({
  left: `${safePosition.value.x}px`,
  top: `${safePosition.value.y}px`,
  transform: 'translateX(-50%)',
}));
</script>

<template>
  <Teleport to="body">
    <!-- Transparent Backdrop for click-outside -->
    <div v-if="show" class="fixed inset-0 z-[99]" @click="emit('close')" />

    <Transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0 scale-95 translate-y-2"
      enter-to-class="opacity-100 scale-100 translate-y-0"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div
        v-if="show"
        ref="popoverRef"
        class="color-popover fixed z-[100] bg-white/90 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl shadow-black/10 overflow-hidden flex flex-col"
        :style="[popoverStyle, { width: `${POPOVER_WIDTH}px` }]"
        @click.stop
        @mousedown.stop
      >
        <!-- Header & Preview Section -->
        <div class="p-4 pb-2">
          <div class="flex items-center gap-3 mb-4">
            <!-- Large Preview Circle -->
            <div
              class="w-10 h-10 rounded-full shadow-sm ring-1 ring-black/5 flex-shrink-0 transition-colors duration-200"
              :style="getSwatchStyle(normalizedColor)"
            ></div>

            <!-- Input Area -->
            <div class="flex-1 relative group">
              <span
                class="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 font-mono text-sm pointer-events-none select-none"
                >#</span
              >
              <input
                ref="inputRef"
                :value="localColor.replace('#', '')"
                @input="handleInput"
                @keydown="handleKeydown"
                @focus="isInputFocused = true"
                @blur="isInputFocused = false"
                type="text"
                maxlength="6"
                class="w-full bg-neutral-100/50 hover:bg-neutral-100 focus:bg-white border-0 rounded-lg py-2.5 pl-7 pr-8 text-sm font-mono text-neutral-800 focus:ring-2 focus:ring-blue-500/20 transition-all uppercase tracking-wider placeholder-neutral-300"
                placeholder="000000"
              />
              <!-- EyeDropper Button -->
              <button
                v-if="hasEyeDropper"
                class="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-neutral-400 hover:text-neutral-700 rounded-md hover:bg-black/5 transition-colors"
                title="Pick color from screen"
                @click="handleEyeDropper"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="m2 22 1-1h3l9-9" />
                  <path d="M3 21v-3l9-9" />
                  <path d="m15 6 3.4-3.4a2.1 2.1 0 1 1 3 3L5 21l-3 1 1-3L15 6Z" />
                </svg>
              </button>
            </div>
          </div>

          <div class="h-px bg-neutral-100 w-full mb-1"></div>
        </div>

        <!-- Presets Grid -->
        <div class="px-4 pb-4 flex-1 overflow-y-auto custom-scrollbar">
          <div
            class="text-[10px] font-semibold text-neutral-400 uppercase tracking-widest mb-3 pl-0.5"
          >
            {{ $t('editor.presetColors') || 'Palette' }}
          </div>

          <div class="grid grid-cols-8 gap-2">
            <button
              v-for="color in presetColors"
              :key="color"
              class="group relative w-full pt-[100%] rounded-full cursor-pointer focus:outline-none"
              @click="selectPreset(color)"
              :title="color"
            >
              <div
                class="absolute inset-0 rounded-full transition-all duration-200 transform group-hover:scale-110 group-focus:scale-110"
                :class="{
                  'ring-2 ring-offset-2 ring-neutral-900 z-10 scale-110':
                    normalizedColor.toLowerCase() === color.toLowerCase(),
                  'hover:shadow-md': normalizedColor.toLowerCase() !== color.toLowerCase(),
                }"
                :style="getSwatchStyle(color)"
              ></div>
            </button>
          </div>
        </div>

        <!-- Footer Actions -->
        <div
          class="p-3 border-t border-neutral-100 bg-neutral-50/50 flex items-center justify-between gap-2"
        >
          <button
            class="p-2 text-neutral-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors group"
            :title="$t('common.delete') || 'Delete'"
            @click="handleDelete"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="transition-transform group-hover:scale-110"
            >
              <path d="M3 6h18" />
              <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
              <path d="M8 6V4c0-1 1-2 2-2h4c0 1 1 2 2 2v2" />
            </svg>
          </button>

          <div class="flex items-center gap-2">
            <button
              class="px-3 py-2 text-xs font-medium text-neutral-500 hover:text-neutral-800 hover:bg-neutral-100 rounded-lg transition-colors"
              @click="emit('close')"
            >
              {{ $t('common.cancel') || 'Cancel' }}
            </button>
            <button
              class="px-4 py-2 text-xs font-semibold text-white bg-neutral-900 hover:bg-black rounded-lg shadow-sm hover:shadow transition-all transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="!isValid"
              @click="handleApply"
            >
              {{ $t('common.apply') || 'Apply Color' }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.color-popover {
  /* Glassmorphism subtle border */
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.03),
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 8px 10px -6px rgba(0, 0, 0, 0.05);
}

.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.2);
}
</style>
